import Head from 'next/head';
import { useMutation } from '@tanstack/react-query';
import { Button, Status } from '@components';
import { DqpAccountService } from '@data';
import { useLogout } from '@utils';

import ownStyles from './dqpAccountClosure.module.scss';
import styles from '@pages/account.module.scss';

export interface DqpAccountClosureProps {
	onClickBack?: () => void;
}

export const DqpAccountClosure = ({
	onClickBack = () => {
		return;
	},
}: DqpAccountClosureProps) => {
	// Hooks
	const autoLogout = useLogout(
		'Your WaterIQ account has been successfully closed. You have been sent a confirmation email with details of your account closure.',
		2000,
	);

	// Data
	const closeAccountAsDQP = useMutation({
		mutationFn: () => DqpAccountService.closeAccount(),
	});

	// Helpers
	const onConfirmClick = async () => {
		await closeAccountAsDQP.mutateAsync().then(() => autoLogout());
	};

	const StatusComponent = () => {
		return closeAccountAsDQP.isError ? (
			<div className={ownStyles.status}>
				<Status
					status="error"
					show={true}
					onClose={closeAccountAsDQP.reset}
					title={undefined}
					msg="Submission failed due to server error. Please try again later."
				/>
			</div>
		) : (
			<></>
		);
	};

	return (
		<>
			<Head>
				<title>WaterIQ - Close your account</title>
				<meta name="description" content="WaterIQ - Close your account" />
			</Head>
			<main>
				<div className={styles['title-container']}>
					<h1 className={styles.title} data-testid="pageTitle">
						Close your account
					</h1>
				</div>
				<div className={styles.main}>
					<div className={ownStyles.content}>
						<p>
							{`We're sorry to see you go. If you're sure about closing your WaterIQ DQP account, please
				click confirm account closure below.`}
						</p>
						<StatusComponent />
						<div className={ownStyles['buttons-container']}>
							<Button
								key="cancel"
								type="secondary"
								size="auto"
								onClick={onClickBack}
								disabled={closeAccountAsDQP.isPending}
								data-testid="cancel"
							>
								Cancel
							</Button>
							<Button
								size="auto"
								type="primary"
								onClick={onConfirmClick}
								isLoading={closeAccountAsDQP.isPending}
								data-testid="confirm"
							>
								Confirm account closure
							</Button>
						</div>
					</div>
				</div>
			</main>
		</>
	);
};
